/**
 * LegalPage — public Terms of Service (/terms) and Privacy Policy (/privacy)
 * pages for InvictaFlow. Needed as required URLs for TikTok/Meta/LinkedIn
 * developer app reviews.
 */
import { Link, useLocation } from "react-router-dom";

const LAST_UPDATED = "Juni 2026";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200">
      <header className="border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg tracking-tight text-white">
            InvictaFlow <span className="text-slate-500 text-xs font-normal">by InvictaX Group</span>
          </Link>
          <nav className="flex gap-4 text-xs text-slate-400">
            <Link to="/terms" className="hover:text-white">Nutzungsbedingungen</Link>
            <Link to="/privacy" className="hover:text-white">Datenschutz</Link>
          </nav>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-xs text-slate-500 mb-10">Zuletzt aktualisiert: {LAST_UPDATED}</p>
        <div className="space-y-8 text-sm leading-relaxed [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <footer className="mt-16 pt-6 border-t border-slate-800 text-xs text-slate-500">
          InvictaFlow — ein internes Marketing-Betriebssystem der InvictaX Group, Schweiz.
        </footer>
      </main>
    </div>
  );
}

function Terms() {
  return (
    <Shell title="Nutzungsbedingungen (Terms of Service)">
      <section>
        <h2>1. Geltungsbereich und Dienst</h2>
        <p>
          InvictaFlow ist eine Marketing-Management-Plattform («Dienst») der InvictaX Group («wir»). Der Dienst ermöglicht
          autorisierten Benutzern, Social-Media-Inhalte zu planen, zu erstellen und auf verbundenen Konten
          (u. a. Facebook, Instagram, TikTok, LinkedIn, Google Ads) zu veröffentlichen sowie Leads und Kampagnen-Performance zu verwalten.
        </p>
      </section>
      <section>
        <h2>2. Zugang und Konten</h2>
        <ul>
          <li>Der Zugang ist auf eingeladene Benutzer der InvictaX Group und ihrer Tochterfirmen beschränkt.</li>
          <li>Benutzer sind für die Vertraulichkeit ihrer Zugangsdaten verantwortlich.</li>
          <li>Wir können Konten bei Missbrauch jederzeit sperren.</li>
        </ul>
      </section>
      <section>
        <h2>3. Verbindung von Drittkonten</h2>
        <p>
          Benutzer können eigene Social-Media-Konten per OAuth verbinden. Die Veröffentlichung von Inhalten erfolgt
          ausschliesslich im Auftrag und mit Zustimmung des Kontoinhabers. Es gelten zusätzlich die Nutzungsbedingungen
          der jeweiligen Plattform (Meta, TikTok, LinkedIn, Google). Die Verbindung kann jederzeit in InvictaFlow oder
          in den Einstellungen der jeweiligen Plattform widerrufen werden.
        </p>
      </section>
      <section>
        <h2>4. Inhalte</h2>
        <p>
          Benutzer bleiben Inhaber der von ihnen erstellten oder hochgeladenen Inhalte. Benutzer sichern zu, dass
          veröffentlichte Inhalte keine Rechte Dritter verletzen und den Richtlinien der Zielplattformen entsprechen.
        </p>
      </section>
      <section>
        <h2>5. Verfügbarkeit und Haftung</h2>
        <p>
          Der Dienst wird «wie besehen» bereitgestellt. Wir übernehmen keine Gewähr für unterbrechungsfreie Verfügbarkeit,
          insbesondere nicht für Ausfälle von Drittplattform-APIs. Die Haftung für indirekte Schäden ist im gesetzlich
          zulässigen Rahmen ausgeschlossen.
        </p>
      </section>
      <section>
        <h2>6. Anwendbares Recht</h2>
        <p>Es gilt schweizerisches Recht. Gerichtsstand ist der Sitz der InvictaX Group.</p>
      </section>
      <section>
        <h2>7. Kontakt</h2>
        <p>Fragen zu diesen Bedingungen: Kontaktiere die Administration der InvictaX Group über die im Impressum der Firmen-Website angegebenen Kanäle.</p>
      </section>
    </Shell>
  );
}

function Privacy() {
  return (
    <Shell title="Datenschutzerklärung (Privacy Policy)">
      <section>
        <h2>1. Verantwortliche Stelle</h2>
        <p>
          Verantwortlich für die Datenbearbeitung im Rahmen von InvictaFlow ist die InvictaX Group, Schweiz.
          Es gelten das Schweizer Datenschutzgesetz (revDSG) und, soweit anwendbar, die EU-DSGVO.
        </p>
      </section>
      <section>
        <h2>2. Welche Daten wir bearbeiten</h2>
        <ul>
          <li><b>Benutzerkonten:</b> Name, E-Mail-Adresse, verschlüsseltes Passwort, Rolle.</li>
          <li><b>Social-Media-Verbindungen:</b> OAuth-Zugriffstokens, Konto-IDs und Profilnamen der verbundenen Konten (Facebook, Instagram, TikTok, LinkedIn, Google Ads). Tokens werden verschlüsselt gespeichert.</li>
          <li><b>Inhalte:</b> Erstellte Texte, Bilder und Videos sowie Veröffentlichungs-Pläne.</li>
          <li><b>Lead-Daten:</b> Über öffentliche Formulare übermittelte Angaben (z. B. Name, E-Mail, Telefon, Nachricht) inkl. UTM-Parameter.</li>
          <li><b>Website-Analytics:</b> Pseudonymisierte Nutzungsdaten (Seitenaufrufe, Session-ID) ohne Cookies von Drittanbietern.</li>
        </ul>
      </section>
      <section>
        <h2>3. Zwecke der Bearbeitung</h2>
        <ul>
          <li>Planung und Veröffentlichung von Social-Media-Inhalten im Auftrag des Kontoinhabers.</li>
          <li>Verwaltung und Qualifizierung von Kundenanfragen (Leads).</li>
          <li>Auswertung von Marketing-Performance (Reichweite, Kosten, Conversions).</li>
          <li>Betrieb, Sicherheit und Fehleranalyse der Plattform.</li>
        </ul>
      </section>
      <section>
        <h2>4. Weitergabe an Dritte</h2>
        <p>Daten werden nur weitergegeben, soweit dies für den Dienst erforderlich ist:</p>
        <ul>
          <li><b>Meta, TikTok, LinkedIn, Google:</b> Übermittlung von Inhalten und Conversion-Daten an die vom Benutzer verbundenen Konten über die offiziellen APIs.</li>
          <li><b>KI-Anbieter (OpenAI, Google Gemini):</b> Textentwürfe/Prompts zur Content-Generierung, ausschliesslich über eigene API-Schlüssel des Betreibers.</li>
          <li><b>E-Mail-Versand:</b> Für Berichte und System-Benachrichtigungen.</li>
        </ul>
        <p>Es findet kein Verkauf von Personendaten statt.</p>
      </section>
      <section>
        <h2>5. Speicherdauer und Löschung</h2>
        <p>
          Daten werden gespeichert, solange sie für die genannten Zwecke erforderlich sind. Social-Media-Verbindungen
          können jederzeit getrennt werden; zugehörige Tokens werden dabei gelöscht. Lead- und Benutzerdaten werden auf
          Anfrage gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
        </p>
      </section>
      <section>
        <h2>6. Datensicherheit</h2>
        <p>
          Übertragung ausschliesslich verschlüsselt (TLS). Zugriffstokens und Secrets werden verschlüsselt in der
          Datenbank abgelegt. Der Zugriff ist rollenbasiert auf autorisierte Benutzer beschränkt.
        </p>
      </section>
      <section>
        <h2>7. Deine Rechte</h2>
        <p>
          Du hast das Recht auf Auskunft, Berichtigung, Löschung und Datenherausgabe. Wende dich dazu an die
          Administration der InvictaX Group über die im Impressum der Firmen-Website angegebenen Kanäle.
        </p>
      </section>
    </Shell>
  );
}

export default function LegalPage() {
  const { pathname } = useLocation();
  return pathname.includes("privacy") ? <Privacy /> : <Terms />;
}
